import { Globe } from 'lucide-react';
import { useMemo, useState } from 'react';

import { BrightDataProxyConfigDialog } from './BrightDataProxyConfigDialog';
import { useOsintCredentialStatuses } from './hooks/useOsintCredentialStatuses';
import {
  InfraCredentialCard,
  type InfraCredentialSource,
} from './InfraCredentialCard';

/**
 * Proxy / Infrastructure — system-wide (Global) infrastructure credentials such
 * as the Bright Data proxy.
 *
 * Renders the same `InfraCredentialCard` (Configured/Not-Configured chip + a
 * short acceptable-use caveat) as the Human Intelligence sources, but opens the
 * dedicated `BrightDataProxyConfigDialog` on Edit instead of the shared
 * single-field `InfraCredentialConfigDialog`: Bright Data hands operators four
 * separate values (Host/Port/Username/Password), so the dialog collects them
 * individually and assembles the `http://user:pass@host:port` proxy URL
 * client-side before saving it as the `brightdata_proxy` master key
 * (`PUT /enrichment/brightdata_proxy/key`). The backend validator checks the
 * assembled URL on save; any error surfaces through the dialog's inline error
 * callout.
 *
 * `SOURCES` is the frontend allowlist (single source of truth for which creds
 * this panel surfaces); each source's configured flag comes from a per-plugin
 * `GET /enrichment/{plugin}/key`.
 */

const SOURCES: readonly InfraCredentialSource[] = [
  {
    id: 'brightdata_proxy',
    label: 'Bright Data Proxy',
    description:
      'Residential and datacenter proxy network for outbound collection.',
    Icon: Globe,
    warning: 'Web scraping only, not for password spraying.',
  },
] as const;

const SOURCE_IDS = SOURCES.map(source => source.id);

export function InfrastructureCredentialsPanel() {
  const { data, status, refetch } = useOsintCredentialStatuses(SOURCE_IDS);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const configuredById = useMemo(() => {
    const map = new Map<string, boolean>();
    if (status === 'success') {
      for (const credential of data) {
        map.set(credential.id, credential.configured);
      }
    }
    return map;
  }, [data, status]);

  const isError = status === 'error';
  const selected = SOURCES.find(source => source.id === selectedId) ?? null;

  return (
    <div className="flex flex-col gap-6">
      <section>
        <div className="mb-3">
          <h2 className="text-chariot-text-primary text-sm font-semibold">
            Proxy / Infrastructure
          </h2>
          <p className="text-chariot-text-secondary mt-1 text-xs">
            Shared global infrastructure credentials (proxy, email delivery)
          </p>
        </div>

        {isError && (
          <div className="mb-3 flex items-center gap-2 text-xs">
            <span className="text-chariot-status-warning">
              Couldn&apos;t load credential status.
            </span>
            <button
              type="button"
              onClick={() => refetch()}
              className="text-chariot-text-primary underline underline-offset-2 hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2 lg:grid-cols-3">
          {SOURCES.map(source => (
            <InfraCredentialCard
              key={source.id}
              source={source}
              configured={configuredById.get(source.id) ?? false}
              onEdit={() => setSelectedId(source.id)}
            />
          ))}
        </div>
      </section>

      <BrightDataProxyConfigDialog
        source={
          selected
            ? {
                id: selected.id,
                label: selected.label,
                Icon: selected.Icon,
                configured: configuredById.get(selected.id) ?? false,
              }
            : null
        }
        onOpenChange={open => !open && setSelectedId(null)}
      />
    </div>
  );
}
